import { collection, limit, onSnapshot, orderBy, query, type Timestamp } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { MARKET_PAIRS } from '../data/mockMarket';
import { db } from './firebase';
import type { TxType } from './logTransaction';

interface TxRecord {
  type: TxType;
  hash: string;
  account: string;
  chainId: number;
  details: Record<string, string>;
  createdAt: Timestamp | null;
}

// swap logs only store fromToken/toToken, not a pool id — match them back to a
// known pair (either direction) so swap volume rolls up with LP/farm activity.
function poolLabel(tx: TxRecord): string {
  if (tx.details.pool) return tx.details.pool;
  const { fromToken, toToken } = tx.details;
  if (!fromToken || !toToken) return 'Unknown';
  const known = MARKET_PAIRS.find(
    (p) => (p.base === fromToken && p.quote === toToken) || (p.base === toToken && p.quote === fromToken),
  );
  return known ? known.id : [fromToken, toToken].sort().join('-');
}

const MAX_RECORDS = 500;

export function useTransactionStats() {
  const [records, setRecords] = useState<TxRecord[] | null>(null);

  useEffect(() => {
    const q = query(collection(db, 'transactions'), orderBy('createdAt', 'desc'), limit(MAX_RECORDS));
    return onSnapshot(q, (snapshot) => {
      setRecords(snapshot.docs.map((d) => d.data() as TxRecord));
    });
  }, []);

  if (!records) {
    return { loading: true as const };
  }

  const byType = new Map<TxType, number>();
  const byPool = new Map<string, number>();
  const byUser = new Map<string, { count: number; byType: Map<TxType, number>; lastAt: number }>();

  for (const tx of records) {
    byType.set(tx.type, (byType.get(tx.type) ?? 0) + 1);

    const pool = poolLabel(tx);
    byPool.set(pool, (byPool.get(pool) ?? 0) + 1);

    const user = byUser.get(tx.account) ?? { count: 0, byType: new Map(), lastAt: 0 };
    user.count += 1;
    user.byType.set(tx.type, (user.byType.get(tx.type) ?? 0) + 1);
    const at = tx.createdAt?.toMillis() ?? 0;
    if (at > user.lastAt) user.lastAt = at;
    byUser.set(tx.account, user);
  }

  const popularPools = [...byPool.entries()]
    .map(([pool, count]) => ({ pool, count }))
    .sort((a, b) => b.count - a.count);

  const topUsers = [...byUser.entries()]
    .map(([account, stats]) => ({ account, ...stats }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 20);

  const recent = records.slice(0, 20).map((tx) => ({
    type: tx.type,
    account: tx.account,
    pool: poolLabel(tx),
    hash: tx.hash,
    at: tx.createdAt?.toMillis() ?? 0,
  }));

  return {
    loading: false as const,
    totalCount: records.length,
    uniqueUsers: byUser.size,
    byType,
    popularPools,
    topUsers,
    recent,
  };
}
